import { EmailScannerQuery } from '@1bill-app/services/email-scanner/email-scanner.query';
import { EmailScannerService } from '@1bill-app/services/email-scanner/email-scanner.service';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  EMAIL_SCANNER_INITIAL_CONFIG,
  EMAIL_SCANNER_MAIN_INFO,
  EXPLANATION_NOTES_SIMPLE,
  MOBILE_VIEW_THRESHOLD_PX,
  ROUTE_HOMEPAGE,
  ROUTE_USER_PROFILE,
} from '@1bill-app/constants';
import { NavController, Platform, PopoverController } from '@ionic/angular';
import { SubSink } from 'subsink';
import { PopoverSimpleComponent } from '@1bill-app/components/popover/popover-simple/popover-simple.component';
import { SettingsQuery } from '@1bill-app/services/settings/settings.query';
import { tap } from 'rxjs/operators';
import { SettingsService } from '@1bill-app/services/settings/settings.service';

interface EmailScannerListData {
  isHeader?: boolean;
  name: string;
  key: string;
  active: boolean;
  isValid: boolean;
  route?: string;
  icon?: string;
  fullRoute?: boolean;
}

@Component({
  selector: 'app-email-scanner',
  templateUrl: './email-scanner.page.html',
  styleUrls: ['./email-scanner.page.scss'],
})
export class EmailScannerPage implements OnInit {
  constructor(
    private platform: Platform,
    private query: EmailScannerQuery,
    private svc: EmailScannerService,
    private navCtrl: NavController,
    private route: ActivatedRoute,
    private popoverController: PopoverController,
    private settingsQuery: SettingsQuery,
    private settingsService: SettingsService,
  ) {
    this.emailScannerList = [...EMAIL_SCANNER_INITIAL_CONFIG];
  }

  emailScannerList: EmailScannerListData[] = [];
  isLoading$ = this.query.isLoading$;
  data$ = this.query.emailConfig$;
  _source: string = ROUTE_USER_PROFILE;
  subs = new SubSink();
  emailScannerMainInfo = EMAIL_SCANNER_MAIN_INFO;
  
  readonly BILL_EMAIL_RETRY_ATTEMPTS = 1;
  currentBillEmailAttempts = 0;

  billEmail$ = this.settingsQuery.select('billEmail').pipe(
    tap((value) => {
      if (!value || this.currentBillEmailAttempts++ >= this.BILL_EMAIL_RETRY_ATTEMPTS) {
        // User has most likely refreshed on this page if account settings/bill email is absent.
        this.settingsService.fetch().subscribe();
        return 'Loading your 1bill email...';
      }
    })
  );

  get isDesktopView() {
    return this.platform.width() >= MOBILE_VIEW_THRESHOLD_PX;
  }

  ngOnInit() {
    this.subs.sink = this.svc.fetch().subscribe();
    this.data$.subscribe((res) => {
      this.emailScannerList = this.emailScannerList.map((list) => {
        list.active = Boolean(res.find((item) => item.emailProvider === list.key));
        list.isValid = Boolean(
          res.find((item) => item.emailProvider === list.key && item.isValid === 1),
        );
        return list;
      });
    });

    this.route.queryParams.forEach((param) => {
      const { refetch } = param;
      if (refetch) {
        this.subs.sink = this.svc.fetch().subscribe();
      }
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  goToRoute(routeStr: string, active: boolean) {
    const routeSplit = routeStr.split('/');
    this.navCtrl.navigateForward(
      [routeStr, active ? 'update' : 'create'],
      routeSplit.length > 1
        ? {
            queryParams: {
              providerName: this.emailScannerList.find(
                (emailConfig) => emailConfig.key === routeSplit[1],
              )?.name,
            },
          }
        : undefined,
    );
  }

  async presentInfoPopover() {
    const popover = await this.popoverController.create({
      component: PopoverSimpleComponent,
      cssClass: 'onebill-medium-popover',
      // translucent: true,
      mode: 'ios',
      componentProps: {
        popoverTitle: EXPLANATION_NOTES_SIMPLE.EMAIL_SCANNER_TITLE,
        descriptionArray: EXPLANATION_NOTES_SIMPLE.EMAIL_SCANNER_DESC,
      },
    });
    return await popover.present();
  }
}
